import { supabase } from '../lib/supabase';

const requireClient = () => {
  if (!supabase) throw new Error('Analytics is unavailable because Supabase is not configured.');
};

const startDate = (days) => {
  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() - Math.max(0, days - 1));
  return date.toISOString().slice(0, 10);
};

const messageFor = (error, fallback) => error?.message || fallback;

export const platformAnalyticsService = {
  async getAccounts(profileId, platform) {
    requireClient();
    if (!profileId) return [];
    const { data, error } = await supabase.rpc('get_automation_accounts', {
      p_profile_id: profileId,
      p_platform: platform,
    });
    if (error) throw new Error(messageFor(error, 'Unable to load connected accounts.'));
    return (data || []).map((account) => ({
      id: String(account.account_id),
      name: account.account_name || 'Connected account',
    }));
  },

  async getActivity({ profileId, platform, accountId = null, days = 7 }) {
    requireClient();
    if (!profileId) return [];
    const { data, error } = await supabase.rpc('get_automation_activity_summary', {
      p_profile_id: profileId,
      p_platform: platform,
      p_account_id: accountId || null,
      p_start_date: startDate(days),
    });
    if (error) throw new Error(messageFor(error, 'Unable to load automation activity.'));
    return (data || []).map((row) => ({
      date: row.activity_date,
      eventType: row.event_type,
      total: Number(row.total || 0),
    }));
  },

  async getInsights({ profileId, platform, accountId = null, days = 7 }) {
    requireClient();
    if (!profileId) return { snapshots: [], states: [] };

    let snapshotsQuery = supabase
      .from('platform_insight_snapshots')
      .select('account_id,metric_name,metric_value,metric_date,captured_at,metadata')
      .eq('profile_id', profileId)
      .eq('platform', platform)
      .gte('metric_date', startDate(days))
      .order('metric_date', { ascending: true })
      .order('id', { ascending: true });

    let statesQuery = supabase
      .from('platform_insight_sync_state')
      .select('account_id,sync_status,available_metrics,error_code,error_message,last_attempt_at,last_synced_at')
      .eq('profile_id', profileId)
      .eq('platform', platform);

    if (accountId) {
      snapshotsQuery = snapshotsQuery.eq('account_id', accountId);
      statesQuery = statesQuery.eq('account_id', accountId);
    }

    const loadSnapshots = async () => {
      const rows = [];
      for (let offset = 0; ; offset += 1000) {
        const result = await snapshotsQuery.range(offset, offset + 999);
        if (result.error) return result;
        rows.push(...(result.data || []));
        if ((result.data || []).length < 1000) return { data: rows, error: null };
      }
    };
    const [snapshotsResult, statesResult] = await Promise.all([loadSnapshots(), statesQuery]);
    if (snapshotsResult.error) {
      throw new Error(messageFor(snapshotsResult.error, 'Unable to load performance snapshots.'));
    }
    if (statesResult.error) {
      throw new Error(messageFor(statesResult.error, 'Unable to load synchronization status.'));
    }

    return {
      snapshots: (snapshotsResult.data || []).map((row) => ({
        ...row,
        metric_value: Number(row.metric_value),
      })),
      states: statesResult.data || [],
    };
  },

  async refreshInsights({ profileId, platform, accountId = null }) {
    requireClient();
    const { data, error } = await supabase.functions.invoke('sync-platform-insights', {
      body: {
        profileId,
        platform,
        ...(accountId ? { accountId } : {}),
      },
    });
    if (error) {
      const context = await error.context?.json?.().catch(() => null);
      throw new Error(context?.error || messageFor(error, 'Insights refresh failed.'));
    }
    return data;
  },
};

export { startDate };
